import type { Layout } from '../data/types';
import type { Ring, Vec2 } from '../geo/types';
import { WORLD } from '../core/config';
import { nieuweBrugWestHead, NIEUWE_BRUG_WEST_STAIRS } from './nieuweBrugWestHead';

export interface WaterAccess {
  readonly id: string;
  readonly origin: Vec2;
  readonly heading: number;
  readonly width: number;
  readonly landing: number;
  readonly treads: number;
  readonly rise: number;
  readonly going: number;
  readonly topY: number;
  readonly footY: number;
  /** Two adjacent exposed landing faces share continuous L-shaped treads. */
  readonly corner?: true;
  readonly landingBackWidth?: number;
}

/** Visible painted water stairs. Ground dimensions and riser counts are reconstruction estimates. */
export function waterAccesses(layout: Layout): readonly WaterAccess[] {
  const enabled = new Set(layout.landmarks.map((l) => l.id));
  const west = nieuweBrugWestHead(layout);
  return [
    {
      id: 'nieuwebrug-west-water-stairs',
      landmark: 'landmark-nieuwe-brug-west-tower',
      // The tower, supported corner landing and wrap share one bridge-based frame.
      origin: west?.origin ?? ([369.500302, 460.156396] as Vec2),
      heading: west?.heading ?? 0.4713,
      width: west?.width ?? 5.5,
      landing: west?.landing ?? 1.5,
      corner: true as const,
      landingBackWidth: west?.landingBackWidth ?? 2.75,
      treads: NIEUWE_BRUG_WEST_STAIRS.treads,
      rise: NIEUWE_BRUG_WEST_STAIRS.rise,
      going: NIEUWE_BRUG_WEST_STAIRS.going,
      topY: 0,
      footY: -2.5,
    },
    {
      id: 'nieuwebrug-east-water-stairs',
      landmark: 'landmark-nieuwe-brug-east-headhouse',
      // Meet the exact east bank, so the landing closes the bank wings without a seam.
      origin: [468.97789, 380.043988] as Vec2,
      heading: 1.7189,
      width: 3.2,
      landing: 1.5,
      treads: 4,
      rise: 0.2,
      going: 0.55,
      topY: 0,
      footY: -2.5,
    },
    {
      id: 'martelaars-water-stairs',
      landmark: 'landmark-martelaars-quay-tower',
      origin: [304.0, 572.0] as Vec2,
      heading: 0.55,
      width: 3.3,
      landing: 1.5,
      treads: 4,
      rise: 0.2,
      going: 0.55,
      topY: 0,
      footY: -2.5,
    },
  ].filter((flight) => enabled.has(flight.landmark));
}

export function waterAccessFootprint(flight: WaterAccess): Ring {
  const c = Math.cos(flight.heading),
    s = Math.sin(flight.heading);
  const at = (u: number, v: number): Vec2 => [
    flight.origin[0] + u * c - v * s,
    flight.origin[1] + u * s + v * c,
  ];
  const half = flight.width / 2,
    run = flight.going * flight.treads;
  // Only treads above the water count as walking surfaces; the submerged flight stays visible.
  const dryRun = Math.min(
    run,
    Math.max(0, Math.ceil((flight.topY - WORLD.waterY) / flight.rise - 1e-6) - 1) * flight.going
  );
  if (flight.corner)
    return [
      at(-flight.landing, -half - dryRun),
      at(dryRun, -half - dryRun),
      at(dryRun, half),
      at(0, half),
      at(0, flight.landingBackWidth ?? half),
      at(-flight.landing, flight.landingBackWidth ?? half),
    ];
  return [
    at(-flight.landing, -half),
    at(dryRun, -half),
    at(dryRun, half),
    at(-flight.landing, half),
  ];
}

export function waterAccessHeight(flight: WaterAccess, p: Vec2): number | null {
  const dx = p[0] - flight.origin[0],
    dy = p[1] - flight.origin[1];
  const c = Math.cos(flight.heading),
    s = Math.sin(flight.heading);
  const u = dx * c + dy * s,
    v = -dx * s + dy * c;
  if (flight.corner) {
    const half = flight.width / 2,
      run = flight.going * flight.treads;
    if (
      u < -flight.landing ||
      u > run ||
      v < -half - run ||
      v > (u <= 0 ? (flight.landingBackWidth ?? half) : half)
    )
      return null;
    const distance = Math.max(u, -v - half);
    return distance < 0
      ? flight.topY
      : flight.topY -
          Math.min(flight.treads, Math.floor(distance / flight.going) + 1) * flight.rise;
  }
  if (Math.abs(v) > flight.width / 2 || u < -flight.landing || u > flight.going * flight.treads)
    return null;
  return u < 0
    ? flight.topY
    : flight.topY - Math.min(flight.treads, Math.floor(u / flight.going) + 1) * flight.rise;
}
